import { useQuery } from "@tanstack/react-query";

import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import {
  buildAnalyticsTags,
  buildCatalogFilters,
  CATALOG_CARD_ATTRIBUTES,
  type CatalogHit,
  getPrimarySearchIndexName,
  isCatalogHit,
  objectIdFilter,
} from "@/utils/algoliaCatalog";
import { getAlgoliaSearchClient } from "@/utils/algoliaSearchClient";
import { getAdonisTopRecommendationsId } from "api/methods";
import type { CatalogSummaryInput } from "hooks/useCatalogSummary";
import { catalogFilterInputKey, useCatalogFilterInput } from "hooks/useCatalogFilterInput";

/**
 * The patient's ranked recommendation ids. A signed-out visitor gets a 401, which means no
 * recommendations rather than something to retry.
 */
export const recommendationIdsQuery = (limit?: number) => ({
  ...getAdonisTopRecommendationsId.keyed({ limit }),
  staleTime: 1000 * 30,
  retry: false,
});

/** The hits in the order the ids rank them; an id with no visible record is dropped. */
export const orderHitsByIds = (
  ids: readonly string[],
  hits: readonly AlgoliaSearchResult[],
): CatalogHit[] => {
  const hitsById = new Map(hits.filter(isCatalogHit).map((hit) => [hit.objectID, hit]));
  return ids.map((id) => hitsById.get(id)).filter((hit): hit is CatalogHit => !!hit);
};

/** One query for exactly these records, under the same tenancy and visibility filter as the shop. */
export const fetchCatalogHitsByIds = async (
  input: CatalogSummaryInput,
  ids: readonly string[],
): Promise<CatalogHit[]> => {
  if (ids.length === 0) {
    return [];
  }

  const { results } = await getAlgoliaSearchClient().search<AlgoliaSearchResult>({
    requests: [
      {
        indexName: getPrimarySearchIndexName(),
        query: "",
        hitsPerPage: ids.length,
        attributesToRetrieve: CATALOG_CARD_ATTRIBUTES,
        attributesToHighlight: [],
        filters: `${buildCatalogFilters(input)} AND ${objectIdFilter(ids)}`,
        // A lookup by id, not a search a patient ran: keep it out of the search analytics.
        analytics: false,
        analyticsTags: buildAnalyticsTags(input.platformId, "rail", ["rail:recommendations"]),
      },
    ],
  });
  const [result] = results;

  if (!result || !("hits" in result)) {
    throw new Error("Algolia recommendations returned an unexpected response shape.");
  }

  return orderHitsByIds(ids, result.hits);
};

/**
 * Adonis's recommendations as catalog hits, in Adonis's order. `hits` stays undefined while the
 * ids or the records load, and when the ids are refused (a signed-out visitor).
 */
export const useRecommendedCatalogHits = (limit?: number) => {
  const ids = useQuery(recommendationIdsQuery(limit));
  const input = useCatalogFilterInput();

  const hits = useQuery({
    queryKey: ["algolia/recommendations", ...catalogFilterInputKey(input), ids.data ?? []],
    queryFn: () => fetchCatalogHitsByIds(input!, ids.data!),
    enabled: !!input && !!ids.data,
    staleTime: 1000 * 30,
    keepPreviousData: true,
  });

  return { hits: hits.data, isLoading: ids.isLoading || hits.isLoading };
};
